import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import getPlansApi from '@/apis/get-plans.api';
import QUERY_KEYS from '@/constants/query-keys';
import useDestination from '@/hooks/use-destination.hook';
import useGetCurrencyConversion from '@/hooks/use-get-currency-conversion.hook';
import useGetGeolocation from '@/hooks/use-get-geolocation.hook';
import { PlanWithLocalCurrency } from '@/types/plan';

export default function useGetPlan() {
  const { geolocation, isPendingGeolocation } = useGetGeolocation();
  const { i18n } = useTranslation();
  const { parseLng } = useDestination();

  const { data: plansData, isLoading: isLoadingPlans } = useQuery({
    queryKey: QUERY_KEYS.PLANS.ALL,
    queryFn: getPlansApi,
    staleTime: 1000 * 60 * 30, // 30 min
  });

  const { rate } = useGetCurrencyConversion(
    'USD',
    geolocation.currency || 'USD'
  );

  const { data: plans, isPending: isPendingPlans } = useQuery({
    queryKey: QUERY_KEYS.PLANS.WITH_CURRENCY(
      geolocation.currency || 'USD',
      i18n.language
    ),
    queryFn: () => {
      if (!plansData?.data) return [];
      const lang = parseLng(i18n.language);
      return plansData.data.map<PlanWithLocalCurrency>(plan => ({
        id: plan.id,
        type: plan.type,
        name: plan.names[lang] || `Plan ${plan.id}`,
        price: rate?.amount ? plan.usdPrice * rate.amount : plan.usdPrice,
        currency: rate.to || 'USD',
        tokens: plan.tokens,
        features: plan.features[lang] || [],
      }));
    },
    enabled: !!plansData?.data && !isPendingGeolocation,
    staleTime: 1000 * 60 * 15, // 15 min
  });

  return {
    isPendingPlans: isLoadingPlans || isPendingPlans,
    plans: plans || [],
  };
}
